/**
 * The Fahrtenbuch: every business trip of a year with its Kilometergeld, Tagesgeld and
 * Nächtigungsgeld (lib/at/travel.ts). The amounts are posted to the journal and carried into the
 * income statement; the CSV is the log the Finanzamt asks to see.
 */
import { Download, Plus, TriangleAlert } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { EmptyState } from "@/components/shared/empty-state";
import { TripDialog } from "@/components/travel/trip-dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useAssets } from "@/hooks/use-assets";
import { useTrips } from "@/hooks/use-trips";
import { tripInput, type Trip } from "@/lib/api/trips";
import { MOTOR_KM_CAP, travelLog } from "@/lib/at/travel";
import { buildCsv, downloadCsv } from "@/lib/export/tables";
import { useWorkspace } from "@/lib/session/workspace-context";
import { formatDate, formatMoney } from "@/lib/utils/format";

export function TripsPage() {
  const { t } = useTranslation();
  const { workspace, canWrite } = useWorkspace();
  const { data: trips, isPending } = useTrips(workspace?.id);
  const { data: assets } = useAssets(workspace?.id);
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const [editing, setEditing] = useState<Trip | null>(null);
  const [creating, setCreating] = useState(false);

  const all = trips ?? [];
  const byId = new Map(all.map((trip) => [trip.id, trip]));
  const first = Math.min(thisYear, ...all.map((trip) => Number(trip.date.slice(0, 4)) || thisYear));
  const years = Array.from({ length: thisYear - first + 2 }, (_, index) => thisYear + 1 - index);
  const log = travelLog(all.map(tripInput)).filter((row) => row.date.startsWith(String(year)));
  const totals = log.reduce(
    (sum, row) => ({
      km: sum.km + (row.vehicle === "none" ? 0 : (row.distanceKm ?? 0)),
      mileage: sum.mileage + row.amounts.mileage,
      perDiem: sum.perDiem + row.amounts.perDiem,
      lodging: sum.lodging + row.amounts.lodging,
      total: sum.total + row.amounts.total,
    }),
    { km: 0, mileage: 0, perDiem: 0, lodging: 0, total: 0 },
  );
  // Kilometergeld only for a vehicle that is not business property (KmGV § 4 Abs 1).
  const businessCar = (assets ?? []).some(
    (asset) =>
      asset.type === "car" &&
      asset.inServiceDate.slice(0, 4) <= String(year) &&
      (!asset.disposalDate || asset.disposalDate.slice(0, 4) >= String(year)),
  );
  const carTrips = log.some((row) => row.vehicle === "car" && row.amounts.mileage > 0);

  function exportCsv() {
    const header = [
      t("trips.date"),
      t("trips.departure"),
      t("trips.endDate"),
      t("trips.return"),
      t("trips.origin"),
      t("trips.destination"),
      t("trips.purpose"),
      t("trips.vehicle"),
      t("trips.odometerStart"),
      t("trips.odometerEnd"),
      t("trips.distance"),
      t("trips.mileage"),
      t("trips.perDiemAmount"),
      t("trips.lodging"),
      t("trips.total"),
    ];
    const rows = log.map((row) => {
      const trip = byId.get(row.id)!;
      return [
        row.date,
        trip.departureTime ?? "",
        row.endDate ?? "",
        trip.returnTime ?? "",
        trip.origin ?? "",
        trip.destination,
        trip.purpose,
        t(`trips.vehicles.${row.vehicle}`),
        trip.odometerStart || "",
        trip.odometerEnd || "",
        row.distanceKm ?? 0,
        row.amounts.mileage / 100,
        row.amounts.perDiem / 100,
        row.amounts.lodging / 100,
        row.amounts.total / 100,
      ];
    });
    downloadCsv(`${workspace?.name ?? "export"}-fahrtenbuch-${year}.csv`, buildCsv(header, rows));
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t("trips.title")}</h1>
          <p className="text-sm text-muted-foreground">{t("trips.subtitle")}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Select value={String(year)} onValueChange={(value) => setYear(Number(value))}>
            <SelectTrigger className="w-28" aria-label={t("trips.year")} data-testid="trip-year">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map((option) => (
                <SelectItem key={option} value={String(option)}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            onClick={exportCsv}
            disabled={log.length === 0}
            data-testid="export-trips"
          >
            <Download /> {t("trips.export")}
          </Button>
          {canWrite && (
            <Button onClick={() => setCreating(true)} data-testid="new-trip">
              <Plus /> {t("trips.new")}
            </Button>
          )}
        </div>
      </div>

      {businessCar && carTrips && (
        <Alert data-testid="business-car">
          <TriangleAlert className="size-4" />
          <AlertDescription>{t("trips.businessCar")}</AlertDescription>
        </Alert>
      )}

      {isPending && <Skeleton className="h-40 w-full" aria-busy="true" />}

      {trips && log.length === 0 && (
        <EmptyState
          title={t("trips.emptyTitle", { year })}
          description={t("trips.emptyText")}
          testId="no-trips"
        />
      )}

      {log.length > 0 && (
        <section className="space-y-3">
          <ul className="divide-y rounded-lg border bg-card" data-testid="trip-list">
            {log.map((row) => {
              const trip = byId.get(row.id)!;
              return (
                <li key={row.id}>
                  <button
                    type="button"
                    onClick={() => setEditing(trip)}
                    data-testid="trip-row"
                    className="grid w-full grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-0.5 px-4 py-3 text-left transition-colors hover:bg-accent/40"
                  >
                    <span className="truncate font-medium">
                      {trip.origin ? `${trip.origin} – ${trip.destination}` : trip.destination}
                    </span>
                    <span className="text-right font-medium num" data-testid="trip-row-total">
                      {formatMoney(row.amounts.total / 100)}
                    </span>
                    <span className="truncate text-xs text-muted-foreground">
                      {formatDate(row.date)} · {trip.purpose}
                    </span>
                    <span className="text-right text-xs whitespace-nowrap text-muted-foreground num">
                      {row.vehicle === "none"
                        ? t(`trips.vehicles.none`)
                        : t("trips.km", { km: row.distanceKm ?? 0 })}
                      {row.overCap && ` · ${t("trips.capped")}`}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
          <dl className="grid gap-3 rounded-lg border bg-card px-4 py-3 text-sm sm:grid-cols-5">
            <div>
              <dt className="text-xs text-muted-foreground">{t("trips.totalKm")}</dt>
              <dd className="font-semibold num" data-testid="trip-total-km">
                {t("trips.km", { km: totals.km })}
              </dd>
              {totals.km > MOTOR_KM_CAP && (
                <dd className="text-xs text-warning">{t("trips.capNote", { km: MOTOR_KM_CAP })}</dd>
              )}
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">{t("trips.mileage")}</dt>
              <dd className="font-semibold num">{formatMoney(totals.mileage / 100)}</dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">{t("trips.perDiemAmount")}</dt>
              <dd className="font-semibold num">{formatMoney(totals.perDiem / 100)}</dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">{t("trips.lodging")}</dt>
              <dd className="font-semibold num">{formatMoney(totals.lodging / 100)}</dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">{t("trips.totalYear", { year })}</dt>
              <dd className="font-semibold num" data-testid="trip-year-total">
                {formatMoney(totals.total / 100)}
              </dd>
            </div>
          </dl>
        </section>
      )}

      <TripDialog open={creating} onOpenChange={setCreating} trips={all} />
      <TripDialog
        open={editing !== null}
        trip={editing}
        trips={all}
        onOpenChange={(open) => !open && setEditing(null)}
      />
    </div>
  );
}
